import { MAX_CUSTOMER_NOTE_LENGTH } from "@daytrip/constants";
import type { ChildSeatType } from "@daytrip/legacy-enums";
import {
    AirportTransferDirection,
    Currency,
    LocationAddressType,
    OfferType,
    OrderStatus,
    OrderType,
    PaymentMethod,
    PricingType,
    VehicleType,
} from "@daytrip/legacy-enums";
import { isUndefinedOrNull } from "@daytrip/utils";
import { Expose, Transform, Type } from "class-transformer";
import type { ValidatorOptions } from "class-validator";
import {
    ArrayNotEmpty,
    IsArray,
    IsBoolean,
    IsDate,
    IsDefined,
    IsEnum,
    IsInt,
    IsNumber,
    IsOptional,
    IsString,
    IsUUID,
    MaxLength,
    ValidateIf,
    ValidateNested,
} from "class-validator";
import { v4 as uuid } from "uuid";

import { GAuthorized, GField, GInputType, GInt, GObjectType } from "../graphql";
import { CDBaseEntity, CDColumn, CDCreateDateColumn, CDEntity, CDObjectIdColumn } from "../orm";
import { statusGetter } from "../statusGetter";
import { TransformToDate } from "../transformers/transformToDate";
import { OrderCancellationInfo } from "../types/OrderCancellationInfo";

import { AffiliateConversion } from "./AffiliateConversion";
import { DynamicPricingInputs } from "./DynamicPricingInputs";
import { Location } from "./Location";
import { OrderAutomaticEmail } from "./OrderAutomaticEmail";
import { OrderCommission } from "./OrderCommission";
import { OrderContentLocation } from "./OrderContentLocation";
import { OrderCustomLocation } from "./OrderCustomLocation";
import type { LeadPassenger } from "./Passenger";
import { Passenger } from "./Passenger";
import { Position } from "./Position";
import { RequestHeader } from "./RequestHeader";
import { TravelEstimates } from "./TravelEstimates";
import { VehicleTypeCoefficient } from "./VehicleTypeCoefficient";
import { VehicleTypePriceFee } from "./VehicleTypePriceFee";

export enum OrderValidationGroup {
    // Anonymous = "Anonymous",
    PriceCalculation = "PriceCalculation",
    BookingOrder = "BookingOrder",
}

export const OrderPriceCalculationValidationOptions: ValidatorOptions = {
    skipMissingProperties: true,
    groups: [OrderValidationGroup.PriceCalculation],
};

@GObjectType("Order")
@GInputType("OrderInput")
@CDEntity("orders")
export class Order extends CDBaseEntity {
    @GField()
    @CDObjectIdColumn()
    @IsUUID("4")
    @Expose()
    public _id: string = uuid();

    @GField()
    @CDCreateDateColumn()
    @IsDefined()
    @Expose()
    @Type(() => Date)
    @Transform((value) => new Date(value))
    public createdAt: Date = new Date();

    @GField()
    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @IsOptional({
        groups: [OrderValidationGroup.PriceCalculation],
    })
    @Expose()
    public userId: string;

    @GField(() => GInt, { nullable: true })
    @CDColumn()
    @IsOptional()
    @IsEnum(OfferType)
    public offerType?: OfferType;

    @GField({ nullable: true })
    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @ValidateIf(
        // So far we support only flexible offers
        // once the same concept of offers is introduced for managed offerings
        // we need to validate offerId in every instance
        (order: Order) => order.offerType === OfferType.Flexible,
    )
    @Expose()
    public offerId?: string;

    @CDColumn()
    @IsUUID("4")
    @Expose()
    @IsOptional()
    public rideId?: string;

    @GField()
    @CDColumn()
    @IsDate()
    @IsDefined()
    @Expose()
    @Type(() => Date)
    @Transform((value) => new Date(value))
    public departureAt: Date = (() => {
        const date = new Date();
        date.setUTCHours(9, 0, 0, 0);
        return date;
    })();

    @GField({ nullable: true })
    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @IsOptional({
        groups: [OrderValidationGroup.BookingOrder],
    })
    @Expose()
    public routeId?: string;

    @GField()
    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public originLocationId: string;

    @GField(() => Location, { nullable: true })
    public originLocation: Location;

    @GField(() => Location, { nullable: true })
    public destinationLocation: Location;

    @GField()
    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public destinationLocationId: string;

    // TODO @MaxLength
    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @Expose()
    public pickupAddress: string = "";

    // TODO @MaxLength
    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @Expose()
    public dropoffAddress: string = "";

    @GAuthorized()
    @GField(() => [Passenger])
    @CDColumn()
    @ArrayNotEmpty()
    @IsArray()
    @IsDefined()
    @ValidateNested()
    @IsOptional({
        groups: [OrderValidationGroup.PriceCalculation],
    })
    @Expose()
    @Type(() => Passenger)
    public passengers: Passenger[] = [];

    @GField(() => [GInt])
    @CDColumn()
    @ArrayNotEmpty()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Number)
    public vehicles: VehicleType[] = [];

    @GField(() => GInt)
    @CDColumn()
    @IsEnum(Currency)
    @IsDefined()
    @Expose()
    public currency: Currency = Currency.Euro;

    @GField({ nullable: true })
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public currencyRate: number = 1;

    @GField(() => GInt, { nullable: true })
    @CDColumn()
    @IsEnum(Currency)
    @IsDefined()
    @Expose()
    public pricingCurrency: Currency = Currency.Euro;

    @GField({ nullable: true })
    @CDColumn()
    @MaxLength(MAX_CUSTOMER_NOTE_LENGTH)
    @IsString()
    @IsDefined()
    @Expose()
    public customerNote: string = "";

    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @IsDefined()
    @Expose()
    public managementNote: string = "";

    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @IsDefined()
    @Expose()
    public driverNote: string = "";

    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @IsOptional()
    @Expose()
    public customQuoteId?: string = "";

    @GField(() => [OrderAutomaticEmail])
    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Transform((value) => value ?? [])
    @Type(() => OrderAutomaticEmail)
    public automaticEmails: OrderAutomaticEmail[] = [];

    @CDColumn()
    @Expose()
    @Type(() => RequestHeader)
    public requestHeader?: RequestHeader;

    @GField(() => GInt)
    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public paymentMethod: PaymentMethod = PaymentMethod.Cash;

    @GField(() => GInt)
    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public type: OrderType = OrderType.Private;

    @GAuthorized()
    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public tollPrice: number = 0;

    @GAuthorized()
    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public tollFee: number = 0;

    @GAuthorized()
    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public additionalPrice: number = 0;

    @GAuthorized()
    @GField()
    @CDColumn()
    @IsNumber()
    @IsDefined()
    @Expose()
    public additionalFee: number = 0;

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @MaxLength(18)
    @IsString()
    @IsDefined()
    @Expose()
    public discountCode: string = ""; // whatever the customer filled out; if accepted, discountId will be in discountIds

    @GAuthorized()
    @GField(() => [String])
    @CDColumn()
    @Expose()
    @Transform((value) => (!value ? [] : value))
    public discountIds: string[] = [];

    @GField({ nullable: true })
    @CDColumn()
    @MaxLength(18)
    @IsString()
    @IsDefined()
    @Expose()
    public referralCode: string = ""; // whatever the customer filled out; if accepted, referralCodeId will be set

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @IsUUID("4")
    @Expose()
    public referralCodeId?: string;

    @GAuthorized()
    @GField(() => [VehicleTypePriceFee], { nullable: true })
    @CDColumn()
    @IsArray()
    @ValidateNested()
    @Expose()
    @Type(() => VehicleTypePriceFee)
    public vehicleTypesPricesFees: VehicleTypePriceFee[] = [];

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @IsNumber()
    @Expose()
    public totalPrice?: number;

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @IsNumber()
    @Expose()
    public price?: number;

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @IsNumber()
    @Expose()
    public fee?: number;

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @IsNumber()
    @Expose()
    // this field is applicable for dynamic pricing. we need it here because some orders do not have offer but they have this price adjustment coefficient
    public priceAdjustmentCoefficient?: number;

    @GAuthorized()
    @GField({ nullable: true })
    @CDColumn()
    @IsNumber()
    @Expose()
    // this field is applicable for dynamic pricing. we need it here because some orders do not have offer but they have this fee adjustment coefficient
    public feeAdjustmentCoefficient?: number;

    @GField(() => [VehicleTypeCoefficient], { nullable: true })
    @CDColumn()
    @Expose()
    @IsArray()
    @Type(() => VehicleTypeCoefficient)
    public vehicleCoefficients?: VehicleTypeCoefficient[] = [];

    @GField(() => String)
    @CDColumn()
    @IsString()
    @Expose()
    public priceType: PricingType = PricingType.Flexible;

    @GField(() => [OrderContentLocation], { nullable: true })
    @CDColumn()
    @IsArray()
    @IsDefined()
    @ValidateNested()
    @Expose()
    @Type(() => OrderContentLocation)
    public contentLocations: OrderContentLocation[] = [];

    @GField(() => [OrderCustomLocation], { nullable: true })
    @CDColumn()
    @IsArray()
    @IsDefined()
    @ValidateNested()
    @Expose()
    @Type(() => OrderCustomLocation)
    public customLocations: OrderCustomLocation[] = [];

    @GField({ nullable: true })
    @CDColumn()
    // @IsDate()
    @Expose()
    @TransformToDate
    public acceptedAt?: Date;

    @GField({ nullable: true })
    @CDColumn()
    // @IsDate()
    @Expose()
    @TransformToDate
    public declinedAt?: Date;

    @GField({ nullable: true })
    @CDColumn()
    // @IsDate()
    @Expose()
    @TransformToDate
    public confirmedAt?: Date;

    @GField({ nullable: true })
    @CDColumn()
    // @IsDate()
    @Expose()
    @TransformToDate
    public cancelledAt?: Date;

    @GField({ nullable: true })
    @CDColumn()
    @Expose()
    @TransformToDate
    public draftedAt?: Date;

    @GField({ nullable: true })
    @CDColumn()
    @IsUUID("4")
    @Expose()
    public createdBy?: string;

    @GField({ nullable: true })
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isPickupAddressApproved: boolean = false;

    @GField({ nullable: true })
    @CDColumn()
    @IsBoolean()
    @IsDefined()
    @Expose()
    public isDropoffAddressApproved: boolean = false;

    @CDColumn()
    @IsBoolean()
    @Expose()
    public isPickupAddressApprovedAutomatically: boolean = false;

    @CDColumn()
    @IsBoolean()
    @Expose()
    public isDropoffAddressApprovedAutomatically: boolean = false;

    @GField(() => GInt, { nullable: true })
    @CDColumn()
    @IsInt()
    @Expose()
    @Transform((value) => (value == null ? undefined : value))
    public pickupAddressType?: LocationAddressType;

    @GField(() => GInt, { nullable: true })
    @CDColumn()
    @IsEnum(AirportTransferDirection)
    @Expose()
    public airportTransferDirection?: AirportTransferDirection;

    @GField(() => GInt, { nullable: true })
    @CDColumn()
    @IsInt()
    @Expose()
    @Transform((value) => (value == null ? undefined : value))
    public dropoffAddressType?: LocationAddressType;

    @GField({ nullable: true })
    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @ValidateIf((order: Order) => order.pickupAddressType === LocationAddressType.MeetingPosition)
    @Expose()
    public meetingPositionId?: string;

    @GField({ nullable: true })
    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @ValidateIf((order: Order) => order.dropoffAddressType === LocationAddressType.MeetingPosition)
    @Expose()
    public dropoffPositionId?: string;

    @CDColumn()
    @IsBoolean()
    @Expose()
    public isFeedbackRequestDisabled?: true;

    @CDColumn()
    @IsBoolean()
    @Expose()
    public departureDateChangedLessThan24hUntilDeparture?: boolean;

    @GField({ nullable: true })
    @CDColumn()
    @IsUUID("4")
    @Expose()
    public pricingCountryId?: string;

    @CDColumn()
    @IsUUID("4")
    @Expose()
    public pricingRegionId?: string;

    @GField()
    @CDColumn()
    @Expose()
    public version: number = 0;

    @GField({ nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional()
    @IsString()
    public apiPartnerId?: string;

    // id in the system of the API partner
    @GField({ nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional()
    @IsString()
    public externalId?: string;

    @GField(() => Position, { nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional()
    @ValidateNested()
    public pickupPosition?: Position | undefined;

    @GField(() => Position, { nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional()
    @ValidateNested()
    public dropoffPosition?: Position | undefined;

    @CDColumn()
    @Expose()
    @IsNumber()
    @IsOptional()
    public customMarkup?: number; // additional markup on top of our price in EUR

    @CDColumn()
    @Expose()
    @IsString()
    @IsOptional()
    public emailVerificationCode?: string;

    @CDColumn()
    @Expose()
    @IsBoolean()
    @IsOptional()
    public isCustomerEmailVerified?: boolean;

    @GField({ nullable: true })
    @IsUUID("4")
    @CDColumn()
    @IsOptional()
    @IsString()
    @Expose()
    public partnerId?: string;

    @GField()
    @CDColumn()
    @IsBoolean()
    @Expose()
    public potentialFraud?: boolean = false;

    @CDColumn()
    @IsString()
    @IsOptional()
    @Expose()
    public gclid?: string;

    @GField({ nullable: true })
    @CDColumn()
    @IsBoolean()
    @IsOptional()
    @Expose()
    public isBusinessTrip?: boolean;

    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @IsOptional()
    @Expose()
    public hostAgencyId?: string;

    @GField({ nullable: true })
    @CDColumn()
    @IsString()
    @IsOptional()
    @Expose()
    public travelAgentId?: string;

    // set only for orders created via Partner API V3
    @CDColumn()
    @IsString()
    @IsOptional()
    @Expose()
    public tripOptionId?: string;

    @GField(() => TravelEstimates, { nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional()
    @ValidateNested()
    public travelEstimates?: TravelEstimates;

    // set only for orders created as a result of affiliate link conversion (via Trackdesk)
    @GField(() => AffiliateConversion, { nullable: true })
    @CDColumn()
    @Expose()
    @IsOptional()
    @ValidateNested()
    public affiliateConversion?: AffiliateConversion;

    @CDColumn()
    @IsBoolean()
    @IsOptional()
    @Expose()
    public isVIP?: boolean = false;

    @CDColumn()
    @Expose()
    @IsOptional()
    @ValidateNested()
    public dynamicPricing?: DynamicPricingInputs;

    @CDColumn()
    @Expose()
    public cancellationInfo?: OrderCancellationInfo;

    @CDColumn()
    @IsOptional()
    @Expose()
    @Type(() => OrderCommission)
    @ValidateNested()
    public commission?: OrderCommission;

    public get leadPassenger(): LeadPassenger {
        let leadPassenger = this.passengers.find((p) => p.isLead()) as LeadPassenger;

        if (!leadPassenger) {
            leadPassenger = this.passengers.find((p) => p.isAdult()) as LeadPassenger;
        }

        return leadPassenger;
    }

    public get status(): OrderStatus {
        return statusGetter(this, Order, OrderStatus);
    }

    public static statusPrototype(status: OrderStatus): Order {
        const now = new Date();

        switch (status) {
            case OrderStatus.Cancelled:
                return {
                    cancelledAt: now,
                } as Order;

            case OrderStatus.Declined:
                return {
                    cancelledAt: undefined,
                    declinedAt: now,
                } as Order;

            case OrderStatus.Draft: {
                return {
                    cancelledAt: undefined,
                    declinedAt: undefined,
                    acceptedAt: undefined,
                    confirmedAt: undefined,
                    draftedAt: now,
                } as Order;
            }

            case OrderStatus.Accepted:
                return {
                    cancelledAt: undefined,
                    declinedAt: undefined,
                    acceptedAt: now,
                    confirmedAt: undefined,
                } as Order;

            case OrderStatus.Confirmed:
                return {
                    cancelledAt: undefined,
                    declinedAt: undefined,
                    acceptedAt: now,
                    confirmedAt: now,
                } as Order;

            case OrderStatus.Pending:
            default:
                return {
                    cancelledAt: undefined,
                    declinedAt: undefined,
                    acceptedAt: undefined,
                    confirmedAt: undefined,
                    draftedAt: undefined,
                } as Order;
        }
    }

    public isCanceled(): boolean {
        return !!this.cancelledAt;
    }

    public get confirmationCode(): string {
        return this._id.slice(0, 6).toUpperCase();
    }

    public get isLite() {
        return this.vehicles.some((vehicleType) => vehicleType === VehicleType.SedanLite);
    }

    public get areAllPassengersAssigned() {
        return this.passengers.every((passenger) => passenger.assignationId);
    }

    public get areSomePassengersAssigned() {
        return this.passengers.some((passenger) => passenger.assignationId);
    }
}

export function getChildSeatsForOrder(order: { passengers: Pick<Order["passengers"][0], "childSeatType">[] }) {
    const childSeats: ChildSeatType[] = [];
    order.passengers.forEach(({ childSeatType }) => {
        if (isUndefinedOrNull(childSeatType)) {
            return;
        }
        childSeats.push(childSeatType);
    });
    return childSeats;
}

export function isAirportTransfer(order: Pick<Order, "airportTransferDirection">) {
    return [AirportTransferDirection.FromAirport, AirportTransferDirection.ToAirport].includes(
        order.airportTransferDirection ?? AirportTransferDirection.None,
    );
}
